import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../design/components/Card';
import { Progress, CircularProgress } from '../design/components/Progress';
import { Badge, DifficultyBadge, GradeBadge, MasteryBadge } from '../design/components/Badge';
import { Button } from '../design/components/Button';
import { Alert } from '../design/components/Alert';
import { BookOpen, Target, Clock, TrendingUp, Award, ChevronRight } from 'lucide-react';

interface LearningProgress {
  totalNodes: number;
  masteredNodes: number;
  inProgressNodes: number;
  dueForReview: number;
  overallMastery: number;
}

interface Recommendation {
  node: {
    id: number;
    code: string;
    name: string;
    difficulty: number;
  };
  priority: number;
  reason: string;
}

export function LearningDashboard() {
  const [progress, setProgress] = useState<LearningProgress | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLearningData();
  }, []);

  async function fetchLearningData() {
    try {
      const [progressRes, recommendRes] = await Promise.all([
        fetch('/api/learning/progress', {
          headers: {
            'X-Auth-User-Id': 'demo-user',
            'X-Auth-User-Email': 'demo@example.com',
          },
        }),
        fetch('/api/learning/recommend', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Auth-User-Id': 'demo-user',
            'X-Auth-User-Email': 'demo@example.com',
          },
          body: JSON.stringify({ maxRecommendations: 5 }),
        }),
      ]);

      if (progressRes.ok) {
        const data = await progressRes.json();
        setProgress(data.progress);
      }

      if (recommendRes.ok) {
        const data = await recommendRes.json();
        setRecommendations(data.recommendations);
      }
    } catch (error) {
      console.error('Failed to fetch learning data:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">学习仪表板</h1>
        <Button variant="outline" size="sm">
          刷新数据
        </Button>
      </div>

      {progress && progress.dueForReview > 0 && (
        <Alert type="warning" title="复习提醒">
          你有 {progress.dueForReview} 个知识点需要复习
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="bordered" className="flex items-center gap-4">
          <div className="p-3 bg-blue-100 rounded-xl">
            <BookOpen className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-slate-500">总知识点</p>
            <p className="text-2xl font-bold text-slate-900">{progress?.totalNodes || 0}</p>
          </div>
        </Card>

        <Card variant="bordered" className="flex items-center gap-4">
          <div className="p-3 bg-green-100 rounded-xl">
            <Target className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-slate-500">已精通</p>
            <p className="text-2xl font-bold text-slate-900">{progress?.masteredNodes || 0}</p>
          </div>
        </Card>

        <Card variant="bordered" className="flex items-center gap-4">
          <div className="p-3 bg-amber-100 rounded-xl">
            <Clock className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-slate-500">待复习</p>
            <p className="text-2xl font-bold text-slate-900">{progress?.dueForReview || 0}</p>
          </div>
        </Card>

        <Card variant="bordered" className="flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-xl">
            <TrendingUp className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="text-sm text-slate-500">总体掌握率</p>
            <p className="text-2xl font-bold text-slate-900">{progress?.overallMastery || 0}%</p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card variant="bordered" className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              推荐学习
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recommendations.length > 0 ? (
              <div className="space-y-3">
                {recommendations.map((rec, index) => (
                  <div
                    key={rec.node.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-semibold text-slate-400">#{index + 1}</span>
                      <div>
                        <p className="font-medium text-slate-900">{rec.node.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="text-xs bg-slate-200 px-1.5 py-0.5 rounded">
                            {rec.node.code}
                          </code>
                          <DifficultyBadge level={rec.node.difficulty as 1 | 2 | 3 | 4 | 5} />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500">
                        {rec.reason === 'weak_area_needs_practice'
                          ? '需要练习'
                          : rec.reason === 'new_concept'
                          ? '新概念'
                          : rec.reason === 'spaced_review_due'
                          ? '复习到期'
                          : '可学习'}
                      </span>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>暂无推荐</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card variant="bordered">
          <CardHeader>
            <CardTitle>学习进度</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center">
              <CircularProgress
                value={progress?.overallMastery || 0}
                size={120}
                strokeWidth={10}
                color={progress?.overallMastery && progress.overallMastery >= 70 ? 'success' : 'primary'}
              />
              <div className="mt-4 w-full space-y-3">
                <Progress
                  value={progress?.masteredNodes || 0}
                  max={progress?.totalNodes || 1}
                  color="success"
                  label="已精通"
                  showLabel
                />
                <Progress
                  value={progress?.inProgressNodes || 0}
                  max={progress?.totalNodes || 1}
                  color="primary"
                  label="学习中"
                  showLabel
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
